import {
  Action,
  PlaybackOptions,
  PlaybackStatistics,
  Player,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackVariantInfo,
  TrackType,
  PlayerContext,
  AdComposedPlayer,
  PLAYBACK_STATES,
  AdEventListener,
  AdPlaybackPolicy,
  AdTechnology,
  AdBreakInfo,
  AdEvent,
} from '@quickplay/player-interface';
import {
  AnyFunc,
  eventObject,
  Error,
  EventEmitter,
  createEventEmitter,
  Logger,
  createLogger,
  LoggerLevel,
  TimeoutController,
  createSafeTimeout,
} from '@quickplay/foundation';
import { MediatailorFactory } from './Mediatailor/MediatailorFactory';
import { MediatailorPlayer } from '../model/MediatailorPlayer';
import { PALConfiguration, PALSession } from '@quickplay/pal';
import { MediatailorAdHandler } from '../model/MediatailorAdHandler';
import { StreamStartUpTimeData } from '../model/StreamStartUpTimeData';
import { MediatailorMetadata } from '../fl-ads-mediatailor';
import {

  AFCPlaybackSession,
  createAFCPlaybackSessionManager,
  AdFrequencyControlConfiguration
} from '@quickplay/player';

export class DefaultMediatailorPlayer implements MediatailorPlayer {
  private readonly mediatailorAdSessionId_: string | undefined;
  private readonly player_: Player | undefined;
  private readonly logger_: Logger | undefined;
  private readonly mediatailorAdHandler: MediatailorAdHandler | undefined;
  private eventEmitter: EventEmitter = createEventEmitter();
  private playerContext_: PlayerContext = PlayerContext.MAIN;
  private palSession_?: PALSession;
  private streamStartUpData?: StreamStartUpTimeData;
  private readonly FALLBACK_TIMEOUT_IN_MS = 500; // safety delay to emit aderror
  private readonly timeoutController: TimeoutController | undefined;
  private afcPlaybackSession?: AFCPlaybackSession;

  constructor(
    player: Player,
    mediatailorMetadata?: MediatailorMetadata,
    palSession?: PALSession,
    adPlaybackPolicy?: AdPlaybackPolicy,
    streamStartUpTimeData?: StreamStartUpTimeData,
    connectionTimeoutInMS?: number,
    maxRetries?: number,
    hlsAdTrackingPollIntervalMS?: number,
    logger?: Logger,
    adFrequencyControl?: AdFrequencyControlConfiguration,
  ) {
    this.player_ = player;
    this.logger_ = logger ?? createLogger(LoggerLevel.OFF);
    this.palSession_ = palSession;
    this.streamStartUpData = streamStartUpTimeData;
    if (mediatailorMetadata && this.isValidMediatailorSession(mediatailorMetadata)) {
      this.mediatailorAdSessionId_ = mediatailorMetadata?.adSessionId;
      this.logger_.log('Initializing Mediatailor Ad Handler');
      this.mediatailorAdHandler = MediatailorFactory.createMediatailorAdHandler(
        this.player_,
        {
          trackingUrl: mediatailorMetadata.trackingUrl!,
          adMetadata: mediatailorMetadata?.adMetadata,
          hlsAdTrackingPollIntervalMS: hlsAdTrackingPollIntervalMS,
          adPlaybackPolicy: adPlaybackPolicy,
          connectionTimeoutMS: connectionTimeoutInMS,
          maxRetries: maxRetries,
        },
        this.logger_,
      );
      this.afcPlaybackSession = adFrequencyControl
        ? createAFCPlaybackSessionManager({
          player: this,
          adFrequencyControlConfiguration: adFrequencyControl,
          logger: this.logger_,
        })
        : undefined;
      this.subscribeToInternalPlayerEvents();
    } else {
      // Set a safety delay to emit aderror. This will give window for the subscribers to subscribe to aderror event event before it is emitted.
      this.timeoutController = createSafeTimeout();
      this.timeoutController.set(() => {
        this.eventEmitter.publish(AdEvent.ADVERT_ERROR, mediatailorMetadata?.fallbackReason);
      }, this.FALLBACK_TIMEOUT_IN_MS);
    }
  }
  adEventListener?: AdEventListener;
  initialize(_initData: PALConfiguration): Promise<string> {
    return Promise.resolve('');
  }
  sendAdClick(): void {
    this.palSession_?.sendAdClick();
  }
  sendAdTouch(clickOrTouchEvent: any): void {
    this.palSession_?.sendAdTouch(clickOrTouchEvent);
  }
  sendPlaybackStart(): void {
    this.palSession_?.sendPlaybackStart();
  }
  sendPlaybackEnd(): void {
    this.palSession_?.sendPlaybackEnd();
  }
  shutdown(): void {
    this.palSession_?.shutdown();
  }
  unloadAndSavePreload(): Promise<void> {
    return Promise.resolve();
  }
  restoreFromPreload(): void {
    // no-op
  }

  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.mediatailorAdHandler?.subscribe(eventName, callback);
    this.player_?.subscribe(eventName, callback);
    this.eventEmitter.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.mediatailorAdHandler?.unsubscribe(eventName, callback);
    this.player_?.unsubscribe(eventName, callback);
    this.eventEmitter.unsubscribe(eventName, callback);
  }

  get adSessionId(): string | undefined {
    return this.mediatailorAdSessionId_;
  }

  get playerContext(): PlayerContext {
    if (this.playerContext_ !== PlayerContext.AD && this.player_ && this.instanceOfAdComposedPlayer(this.player_)) {
      return this.player_.playerContext;
    }

    return this.mediatailorAdHandler?.playerContext() || PlayerContext.MAIN;
  }

  get playerState(): string {
    if (!this.player_) {
      return PLAYBACK_STATES.IDLE;
    }
    return this.player_.playerState;
  }

  get playbackRate(): number {
    if (!this.player_) {
      return 0;
    }
    return this.player_.playbackRate;
  }

  set playbackRate(rate) {
    if (!this.player_) {
      return;
    }
    this.player_.playbackRate = rate;
  }

  get availableBitrates(): number[] {
    return this.player_?.availableBitrates || [];
  }

  set minBitrate(minBitrate) {
    if (!this.player_) {
      return;
    }
    this.player_.minBitrate = minBitrate;
  }

  get minBitrate(): number | undefined {
    return this.player_?.minBitrate;
  }

  set maxBitrate(maxBitrate) {
    if (!this.player_) {
      return;
    }
    this.player_.maxBitrate = maxBitrate;
  }

  get maxBitrate(): number | undefined {
    return this.player_?.maxBitrate;
  }

  get internalPlayerName(): string {
    return this.player_?.internalPlayerName || '';
  }

  get internalPlayerVersion(): string {
    return this.player_?.internalPlayerVersion || '';
  }

  get rawPlayer(): any {
    return this.player_?.rawPlayer;
  }

  get rawLibrary(): any {
    return this.player_?.rawLibrary;
  }

  get isBuffering(): boolean {
    return this.player_?.isBuffering || false;
  }

  get isSeeking(): boolean {
    return this.player_?.isSeeking || false;
  }

  get currentTime(): number {
    return this.mediatailorAdHandler?.currentTime() ?? this.player_?.currentTime ?? 0;
  }

  get currentEpochTime(): number {
    return this.player_?.currentEpochTime || 0;
  }

  get duration(): number {
    return this.mediatailorAdHandler?.duration() ?? this.player_?.duration ?? 0;
  }

  get playbackStatistics(): PlaybackStatistics | undefined {
    if (!this.player_?.playbackStatistics) {
      return undefined;
    }

    return {
      ...this.player_.playbackStatistics,
      ...(this.streamStartUpData && {
        ssaiStreamUrlFetchTimeInMS: this.streamStartUpData.ssaiStreamUrlFetchTimeInMS,
        palTokenFetchTime: this.streamStartUpData.palTokenFetchTimeInMS,
        preRollAdLoadTimeMs: this.mediatailorAdHandler?.getPrerollLoadTimeInMS(),
      }),
    };
  }

  get isLive(): boolean {
    return this.player_?.isLive || false;
  }

  get remainingTime(): number {
    return 0;
  }

  get isMute(): boolean | undefined {
    return this.player_?.isMute;
  }

  get playbackOptions(): PlaybackOptions | undefined {
    return this.player_?.playbackOptions;
  }

  get volume(): number {
    return this.player_?.volume || 0;
  }

  set volume(volumeLevel: number) {
    if (!this.player_) {
      return;
    }
    this.player_.volume = volumeLevel;
  }

  get adInsertionType(): AdTechnology {
    return AdTechnology.SSAI;
  }

  load(): void {
    this.player_?.load();
  }

  setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
    this.player_?.setRequestInterceptor(requestInterceptor);
  }

  setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
    this.player_?.setResponseInterceptor(responseInterceptor);
  }

  accessFunctionHandler(json: string): void {
    this.player_?.accessFunctionHandler(json);
  }

  stop(): Promise<void> {
    this.afcPlaybackSession?.dispose();
    if (!this.player_) {
      return Promise.resolve();
    }
    return this.player_.stop();
  }

  play(): void {
    this.player_?.play();
  }

  pause(): void {
    this.player_?.pause();
  }

  abort(error: Error): void {
    this.player_?.abort(error);
  }

  send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
    this.player_?.send(eventName, action, metadata);
  }

  seek(seekPoint: number): void {
    if (!this.player_) {
      return;
    }
    if (this.player_.isLive || !this.mediatailorAdHandler) {
      this.player_?.seek(seekPoint);
    } else {
      this.mediatailorAdHandler.seek(seekPoint);
    }
  }

  getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    if (!this.player_) {
      return Promise.resolve(undefined);
    }
    return this.player_.getThumbnail(position);
  }

  seekToLiveEdge(): void {
    return this.player_?.seekToLiveEdge();
  }

  seekableRange(): SeekRangeInfo {
    if (this.player_?.isLive) {
      return this.player_.seekableRange();
    }

    return {
      start: 0,
      end: this.duration,
    };
  }

  currentOffsetFromLiveEdge(): number {
    return this.player_?.currentOffsetFromLiveEdge() || 0;
  }

  getAllTracks(): TrackInfo[] {
    return this.player_?.getAllTracks() || [];
  }

  getActiveTracks(): TrackInfo[] {
    return this.player_?.getActiveTracks() || [];
  }

  getVariantTracks(): TrackVariantInfo[] {
    return this.player_?.getVariantTracks() || [];
  }

  getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
    return this.player_?.getAvailableTrackVariants(trackType) || [];
  }

  selectTrack(track?: TrackInfo): void {
    this.player_?.selectTrack(track);
  }

  muteOrUnmute(): void {
    this.player_?.muteOrUnmute && this.player_.muteOrUnmute();
  }

  skipAd(): void {
    // no-op
  }

  resize(_width: number, _height: number, _isFullscreen: boolean): void {
    // no-op
  }

  discardAdBreak(): void {
    // no-op;
  }

  getCuePoints(): number[] {
    if (!this.player_ || this.player_.isLive) {
      return [];
    }
    return this.mediatailorAdHandler?.getCuePointsRelativeToContentTime() || [];
  }

  adClicked(adUrl: string): void {
    this.mediatailorAdHandler?.adClicked(adUrl);
  }

  getAllAdBreaks(): AdBreakInfo[] {
    return this.mediatailorAdHandler?.getAllAdBreaks() || [];
  }

  setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.player_?.setPlaybackOptions(playbackOptions);
  }

  restrictQuality(enableRestriction: boolean): void {
    this.player_?.restrictQuality(enableRestriction);
  }

  availableMimeTypes(trackType: TrackType): Array<string> {
    return this.player_?.availableMimeTypes(trackType) || [];
  }

  setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
    return this.player_?.setPreferredMimeTypes(trackType, mimeTypes);
  }

  private instanceOfAdComposedPlayer(player_: Player): player_ is AdComposedPlayer {
    return 'playerContext' in player_;
  }

  private onPlaybackStateChanged(playbackState: PLAYBACK_STATES): void {
    switch (playbackState) {
      case PLAYBACK_STATES.STARTED: {
        this.sendPlaybackStart();
        break;
      }
      case PLAYBACK_STATES.STOPPED: {
        this.unsubscribeToInternalPlayerEvents();
        this.sendPlaybackEnd();
        break;
      }
      case PLAYBACK_STATES.IDLE: {
        this.unsubscribeToInternalPlayerEvents();
        break;
      }
    }
  }

  private onStreamEnded(): void {
    this.timeoutController?.clear();
    this.sendPlaybackEnd();
  }
  private unsubscribeToInternalPlayerEvents(): void {
    if (!this.player_) {
      return;
    }
    this.timeoutController?.clear();
    this.player_.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged.bind(this));
    this.player_.unsubscribe('streamended', this.onStreamEnded.bind(this));
  }

  private subscribeToInternalPlayerEvents(): void {
    if (!this.player_) {
      return;
    }
    this.player_.subscribe('playbackstatechanged', this.onPlaybackStateChanged.bind(this));
    this.player_.subscribe('streamended', this.onStreamEnded.bind(this));
  }

  private isValidMediatailorSession(mediatailorMetadata?: MediatailorMetadata): boolean {
    if (!mediatailorMetadata) {
      return false;
    }
    return !mediatailorMetadata?.isFallback && mediatailorMetadata?.trackingUrl !== undefined;
  }
}
